/**
 * THE deterministic scoring engine (ARCH §2.2, PRD §3.1).
 *
 * RULE-013: "every path converges on one scoring engine. Free text and
 * transcripts pass through the LLM into typed fields entering the *same*
 * engine with the *same* weight vocabulary. There is no separate 'AI score' to
 * reconcile with a 'rules score'."
 *
 * RULE-026: "the LLM does **not** set the band, does **not** make the
 * qualified/unqualified decision, does **not** score closed-ended answers, and
 * has **no write access to anything**." Everything below is arithmetic over
 * values the operator's rubric asked for. A model can fill an `extracted`
 * field; it cannot reach any other input, and none of them is a band.
 *
 * RULE-027: "failed and unqualified leads are never discarded. A disqualified
 * lead is a nurture lead — store, band, route." So this function has no
 * failure mode that returns nothing: a disqualified lead scores zero, lands in
 * the band that contains zero, and names the gate that stopped it.
 *
 * Pure. No I/O, no bindings, no clock, no randomness — the calibration bench
 * (Story 2.4) runs it ten thousand times in a loop, and the probe (Story 2.5)
 * runs it on answers that are never stored.
 *
 * RULE-001: the computed value is the **score** and the range it falls into is
 * the **band**. `tests/rule-001-score-not-index.test.ts` holds that against
 * this file's source.
 */
import type { Answer, AnswerValue } from './answer'
import type { Funnel } from './funnel'
import {
  type Band,
  type Condition,
  type ConditionOperator,
  type Criterion,
  type CriterionSource,
  type DerivedKey,
  type MetadataKey,
  type ScoringMap,
} from './rubric'

/* ── the answer contract ────────────────────────────────────────────────── */

/**
 * `Answer` and `AnswerValue` are defined in `./answer`, which is the only
 * module that builds them with Zod. They are re-exported here as TYPES —
 * erased at build time — so this file stays free of the validator and the
 * embed runtime can import the engine (Story 5.2). Anything needing the
 * SCHEMAS imports them from `./answer` directly.
 */
export type { Answer, AnswerValue }

/** A typed field the model pulled out of free text (PRD §3.6, M2). The engine
 *  reads `field_key` and `value`; confidence, source span and model belong to
 *  traceability, not to arithmetic. */
export type Fact = { field_key: string; value: AnswerValue }

/** PRD §3.15 attribution, plus `service_area` from `request.cf`. */
export type ScoreMetadata = Partial<Record<MetadataKey, string | number | boolean | null>>

export type ScoreContext = {
  extractions?: Fact[]
  /**
   * Story 20.9, DECISION-138: values computed about the whole submission, which
   * only a `derived` criterion reads. Keyed by `DERIVED_KEYS` — today the ICP
   * fit alone — and numbers only, so no band, flag or sentence has a way in.
   * A fit with no `derived` criterion reading it changes nothing.
   */
  derived?: Partial<Record<DerivedKey, number>>
  /** M5. Reaches the same criteria through the same maps. */
  transcript?: Fact[]
  metadata?: ScoreMetadata
}

/* ── the result ─────────────────────────────────────────────────────────── */

/** One row of the `scores` table, exactly. */
export type Contribution = {
  criterion_id: string
  criterion_label: string
  weight: number
  points_awarded: number
  points_max: number
  source: CriterionSource
}

export type ScoreResult = {
  score: number
  band: Band
  is_qualified: boolean
  /** In rubric order — the order the evidence rows render in. Empty when a
   *  disqualifier fired: `editor-qualify` calls disqualifiers "Hard gates — no
   *  score is calculated", and zeroed rows would claim every criterion was
   *  evaluated and found wanting, which is a different and false statement. */
  contributions: Contribution[]
  /** The id of the disqualifier that fired, or null. Never an exception. */
  disqualified_by: string | null
  /** Its operator-written explanation, for the outcome page and the Verdict. */
  disqualified_reason: string | null
}

/* ── the engine ─────────────────────────────────────────────────────────── */

/**
 * Funnel + answers → score, band, and the per-criterion breakdown.
 *
 * The third argument carries everything that is not a form answer. It is
 * optional because a closed-answer funnel with the AI layer disabled, capped
 * or failing must still score and route correctly (PRD §3.1) — that is the
 * free tier's definition, not a degraded path.
 */
export function score(funnel: Funnel, answers: Answer[], context: ScoreContext = {}): ScoreResult {
  const { rubric } = funnel
  const lookup = buildLookup(answers, context)

  const gate = rubric.disqualifiers.find((disqualifier) =>
    evaluateCondition(disqualifier.when, lookup),
  )
  if (gate !== undefined) {
    const band = resolveBand(rubric.bands, 0)
    return {
      score: 0,
      band,
      is_qualified: false,
      contributions: [],
      disqualified_by: gate.id,
      disqualified_reason: gate.reason ?? null,
    }
  }

  const contributions = rubric.criteria.map((criterion) => contributionOf(criterion, lookup))
  const total = contributions.reduce((sum, c) => sum + c.points_awarded, 0)
  const band = resolveBand(rubric.bands, total)

  return {
    score: total,
    band,
    is_qualified: band.is_qualified,
    contributions,
    disqualified_by: null,
    disqualified_reason: null,
  }
}

function contributionOf(criterion: Criterion, lookup: Lookup): Contribution {
  const value = resolveSubjectValue(criterion, lookup)
  return {
    criterion_id: criterion.id,
    criterion_label: criterion.label,
    weight: criterion.weight,
    // A criterion's weight IS its maximum (see rubric.ts) — so the two columns
    // the `scores` table keeps apart cannot disagree.
    points_max: criterion.weight,
    points_awarded: clamp(award(criterion.points, value), 0, criterion.weight),
    source: criterion.source,
  }
}

/**
 * The band a score falls into.
 *
 * Story 2.2 AC: "overlapping and gapped band definitions are rejected at
 * validation time, not silently resolved." They are — `validateBandLadder` in
 * rubric.ts refuses both — so this function may assume a tiling and does not
 * need a tie-break rule that would be a second, quieter opinion about the same
 * question. Bounds are inclusive at both ends, which is what makes a score
 * landing exactly on a boundary belong to the band that declares it.
 */
export function resolveBand(bands: Band[], value: number): Band {
  const match = bands.find((band) => value >= band.min && value <= band.max)
  if (match !== undefined) return match
  // Unreachable against a validated rubric. Refusing loudly beats inventing a
  // band, because a lead with a band nobody defined is worse than an error.
  throw new Error(
    `no band contains the score ${value} — the rubric's band ladder does not tile 0..${Math.max(...bands.map((b) => b.max))}`,
  )
}

/* ── resolving a value ──────────────────────────────────────────────────── */

type Lookup = {
  answers: Map<string, AnswerValue>
  extractions: Map<string, AnswerValue>
  derived: Partial<Record<DerivedKey, number>>
  transcript: Map<string, AnswerValue>
  metadata: ScoreMetadata
}

function buildLookup(answers: Answer[], context: ScoreContext): Lookup {
  return {
    answers: new Map(answers.map((answer) => [answer.block_id, answer.value])),
    extractions: new Map((context.extractions ?? []).map((fact) => [fact.field_key, fact.value])),
    derived: context.derived ?? {},
    transcript: new Map((context.transcript ?? []).map((fact) => [fact.field_key, fact.value])),
    metadata: context.metadata ?? {},
  }
}

function resolveSubjectValue(criterion: Criterion, lookup: Lookup): AnswerValue | undefined {
  switch (criterion.source) {
    case 'answer_ref':
      return lookup.answers.get(criterion.block_id)
    case 'extracted':
      return lookup.extractions.get(criterion.field_key)
    case 'derived':
      // An own key only. `hasOwnProperty.call` rather than `Object.hasOwn`: this
      // engine also runs in the embed, on browsers older than the dashboard's.
      return Object.prototype.hasOwnProperty.call(lookup.derived, criterion.key)
        ? lookup.derived[criterion.key]
        : undefined
    case 'transcript':
      return lookup.transcript.get(criterion.field_key)
    case 'metadata':
      return lookup.metadata[criterion.key] ?? undefined
  }
}

/* ── what an option answer IS ───────────────────────────────────────────── */

/**
 * The answer a chosen option produces. **Always its `id`** — DECISION-067.
 *
 * THE ONE FUNCTION, imported by everything that has an opinion about this: the
 * embed picks answers with it (`packages/embed/src/blocks.tsx`), the
 * calibration sampler enumerates them with it (`calibrate.ts`), and
 * `crossReference` validates against the same ids. Before this existed the
 * embed said `option.value ?? option.id` and everything else said `option.id`,
 * which is a disagreement that cannot be spotted by reading either one.
 *
 * WHAT THE DISAGREEMENT COST, so it is not reintroduced: an option carrying an
 * explicit `value` different from its `id` was submitted as `value`, scored
 * against a rubric keyed on `id`, matched nothing, and fell through to
 * `default_points`. The option scored zero and nothing reported it — RULE-018's
 * silent-failure shape inside the scoring engine. `crossReference` compounded
 * it by REFUSING the configuration that would have worked and ACCEPTING the one
 * that failed, and `calibrate.ts` sampled ids, so PRD §3.18's bench scored such
 * a funnel the way the rubric intended while production scored it zero.
 *
 * It lives in `scoring.ts` because that is where the answer's meaning belongs,
 * and because this module holds no Zod — `@leadgible/schema/runtime` can
 * re-export it and the embed can import the value without pulling 17.8 KB of
 * validator past `scripts/check-embed-size.mjs`.
 *
 * `value` is not read here and must never be: it is export metadata now
 * (`funnel.ts`, `lib/serialize.ts`), resolved at egress and never on the
 * scoring path.
 *
 * Story 7.6.
 */
export const optionAnswer = (option: { id: string }): string => option.id

/* ── applying a scoring map ─────────────────────────────────────────────── */

/**
 * What a value is worth under one map.
 *
 * An absent value is not an error — a respondent may skip an optional
 * question, and an `extracted` field is absent until the model runs (or
 * forever, if the analysis cap was reached). Every map therefore has a
 * defined answer for "nothing here", and it is the map's own default rather
 * than a hardcoded zero.
 */
export function award(map: ScoringMap, value: AnswerValue | undefined): number {
  switch (map.kind) {
    case 'presence':
      return isPresent(value) ? map.points_present : map.points_absent

    case 'options': {
      if (!isPresent(value)) return map.default_points
      const selected = Array.isArray(value) ? value : [String(value)]
      const matched = selected.filter((option) => option in map.points_by_option)
      if (matched.length === 0) return map.default_points
      // Q-009: multi-select summing is unspecified. Summing is what an
      // operator writing per-option points means; the clamp in
      // `contributionOf` is what keeps `points_awarded <= points_max` true, and
      // the band ceiling with it.
      return matched.reduce((sum, option) => sum + (map.points_by_option[option] ?? 0), 0)
    }

    case 'ranges': {
      const numeric = asNumber(value)
      if (numeric === null) return map.default_points
      // First match wins, as `editor-logic` states for rules: an ordered list
      // whose order is the operator's, not the sorter's.
      const range = map.ranges.find(
        (r) => (r.min === null || numeric >= r.min) && (r.max === null || numeric <= r.max),
      )
      return range?.points ?? map.default_points
    }

    case 'keywords': {
      if (typeof value !== 'string' || value.length === 0) return map.default_points
      const haystack = value.toLowerCase()
      const rule = map.rules.find(
        (r) =>
          (r.min_length === undefined || value.length >= r.min_length) &&
          r.any_of.some((keyword) => haystack.includes(keyword.toLowerCase())),
      )
      return rule?.points ?? map.default_points
    }
  }
}

/* ── conditions ─────────────────────────────────────────────────────────── */

/**
 * Evaluate one condition against the resolved inputs. Shared by disqualifiers
 * here and by `logic[]` in the runtime and the calibration sampler (Story 2.4)
 * — the same reason there is one scoring engine.
 */
export function evaluateCondition(condition: Condition, lookup: Lookup): boolean {
  const { subject } = condition
  const value =
    subject.kind === 'answer'
      ? lookup.answers.get(subject.block_id)
      : subject.kind === 'extracted'
        ? lookup.extractions.get(subject.field_key)
        : (lookup.metadata[subject.key] ?? undefined)

  return applyOperator(condition.operator, value, condition.value)
}

/** The lookup a caller outside this module builds to evaluate conditions. */
export const scoringLookup = (answers: Answer[], context: ScoreContext = {}): Lookup =>
  buildLookup(answers, context)
export type ScoringLookup = Lookup

function applyOperator(
  operator: ConditionOperator,
  actual: AnswerValue | undefined,
  expected: Condition['value'],
): boolean {
  switch (operator) {
    case 'is_empty':
      return !isPresent(actual)
    case 'is_not_empty':
      return isPresent(actual)
    case 'is':
      return matches(actual, expected)
    case 'is_not':
      return !matches(actual, expected)
    case 'is_one_of':
      return asList(expected).some((candidate) => matches(actual, candidate))
    case 'is_not_one_of':
      return !asList(expected).some((candidate) => matches(actual, candidate))
    case 'contains':
      return containsValue(actual, expected)
    case 'not_contains':
      return !containsValue(actual, expected)
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const left = asNumber(actual)
      const right = asNumber(expected as AnswerValue)
      if (left === null || right === null) return false
      if (operator === 'gt') return left > right
      if (operator === 'gte') return left >= right
      if (operator === 'lt') return left < right
      return left <= right
    }
  }
}

/**
 * A multi-select answer is a list, and "Budget is over_100k" against a list
 * means the respondent chose it — not that it was their only choice. Every
 * form builder reads it that way and the alternative silently drops rules an
 * operator wrote for a multi-select question.
 */
function matches(actual: AnswerValue | undefined, expected: unknown): boolean {
  if (actual === undefined || actual === null) return false
  if (Array.isArray(actual)) return actual.some((entry) => sameScalar(entry, expected))
  return sameScalar(actual, expected)
}

function sameScalar(actual: unknown, expected: unknown): boolean {
  if (typeof actual === typeof expected) return actual === expected
  // A card-select value is a string and a number condition is a number; the
  // operator meant them to compare.
  return String(actual) === String(expected)
}

function containsValue(actual: AnswerValue | undefined, expected: unknown): boolean {
  if (actual === undefined || actual === null) return false
  if (Array.isArray(actual)) return actual.some((entry) => sameScalar(entry, expected))
  if (typeof actual === 'string' && typeof expected === 'string') {
    return actual.toLowerCase().includes(expected.toLowerCase())
  }
  return sameScalar(actual, expected)
}

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : [value])

/* ── small shared predicates ────────────────────────────────────────────── */

/** `false` is an answer. A consent block that was ticked "no" was answered. */
export function isPresent(value: AnswerValue | undefined): boolean {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.length > 0
  if (Array.isArray(value)) return value.length > 0
  return true
}

function asNumber(value: AnswerValue | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high)
